// Lib
import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { identity, defer } from 'lodash';
import { pure, withHandlers } from '../../node_module_clones/recompose';
import globalLogger from '../logger/logger';

const logger = globalLogger.createChannel('element.render');

// Util
import { markEventAsHandled } from '../utils/react/reactEventUtil';
import * as elementRegistry from '../../common/elements/elementRegistry';
import { isDOMNodeInView, isPrimaryButton, stopPropagationOnly } from '../utils/domUtil';
import { getEventSelectionMode } from './selection/selectionUtils';
import { isTask, isBoard, isLine, isTaskList } from '../../common/elements/utils/elementTypeUtils';
import makeElementSelector from './selectors/elementSelector';
import { noLonger, now } from '../utils/react/propsComparisons';
import { clearSelection } from '../utils/keyboard/contentEditable';
import { getElementId, getElementType, getLocationParentId } from '../../common/elements/utils/elementPropertyUtils';
import { canFocusElement } from '../workspace/presentation/focusMode/focusModeUtils';
import { isReadOnly } from '../../common/permissions/permissionUtil';
import { hasCommandModifier } from '../utils/keyboard/keyboardUtility';
import { getLocalHidden } from './local/elementLocalDataUtils';
import currentlyVisibleElementsSingleton from './elementVisibility/currentlyVisibleElementsSingleton';
import { getObserverRootOptions } from './elementVisibility/elementContentVisibleObserverUtils';
import { canApplyDragClassManually } from '../workspace/dnd/elementDragUtils';
import { isElementInEditor } from '../components/editor/editorUtils';
import { getCurrentUser } from '../user/currentUserSelector';
import { isFeatureEnabled } from '../../common/users/utils/userPropertyUtils';

// Custom Decorators
import { zoomStateContextConsumer } from '../canvas/zoom/ZoomStateContext';
import { poiBoardSectionContextConsumer } from '../components/pointsOfInterest/PoiBoardSectionContext';
import measureElementDecorator from '../components/measurementsStore/elementMeasurements/measureElementDecorator';
import DraggableElement, { shouldAttachDragSource } from './dnd/DraggableElement';
import ElementPlaceholderDecorator from './elementPlaceholder/ElementPlaceholderDecorator';

// Components
import AnimationLock from '../components/animations/AnimationLock';
import ElementContentVisibleObserver from './elementVisibility/ElementContentVisibleObserver';
import measurementsRegistry from '../components/measurementsStore/measurementsRegistry';

// Actions
import elementContainerMapDispatchToProps from './elementContainerMapDispatchToProps';

// Constants
import { MODIFIER_KEYS } from '../utils/dnd/modifierKeys/dragModifierKeysConstants';
import { ExperimentId } from '../../common/experiments/experimentsConstants';

// Styles
import './Element.scss';

const isNoLongerResizing = noLonger('isResizing');
const isNowDragging = now('isDragging');

const SMALL_VISUAL_VIEWPORT_HEIGHT = 550;

const makeElementContainerStateSelector = () => {
    const elementSelector = makeElementSelector();

    return (state, ownProps) => {
        const currentUser = getCurrentUser(state);
        return {
            ...elementSelector(state, ownProps),
            isSafariThrashingFixEnabled: isFeatureEnabled(ExperimentId.safariScrollThrashing, currentUser),
        };
    };
};

@connect(makeElementContainerStateSelector, elementContainerMapDispatchToProps)
@poiBoardSectionContextConsumer
@zoomStateContextConsumer
@withHandlers({
    stopEditing:
        ({ dispatchStopEditing, element }) =>
        () =>
            element && dispatchStopEditing(getElementId(element)),
})
@DraggableElement
@measureElementDecorator
@ElementPlaceholderDecorator
@pure
class ElementContainer extends React.Component {
    constructor(props) {
        super(props);

        this.preventClick = false;

        this.state = {
            isBatchedRenderInProgress: false,
        };
    }

    componentDidMount() {
        const { connectDragPreview } = this.props;
        if (connectDragPreview) {
            connectDragPreview(getEmptyImage());
        }

        const domNode = ReactDOM.findDOMNode(this);
        if (!domNode) return;

        if (this.props.isSelected) {
            if (!isDOMNodeInView(domNode)) domNode.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
    }

    componentDidUpdate(prevProps) {
        // Prevent a click which occurs immediately after a resize from selecting the element
        if (isNoLongerResizing(prevProps, this.props)) {
            this.preventClick = true;
            defer(() => {
                this.preventClick = false;
            });
        }
    }

    componentWillUnmount() {
        currentlyVisibleElementsSingleton.remove(this.props.elementId);
    }

    shouldComponentUpdate(nextProps) {
        if (nextProps.animating) return false;

        // Any time it is a copy/alt drag, do a regular render as a different class should be applied.
        if (nextProps.dragModifierKeys.get(MODIFIER_KEYS.altKey)) return true;

        // This is to avoid re-rendering elements when isDragging is set to TRUE for performance improvements,
        // We don't prevent render when isDragging is set to false as other props may have changed that require a re-render
        if (isNowDragging(this.props, nextProps) && canApplyDragClassManually(this.props.element)) return false;

        return true;
    }

    /**
     * If the element receives the mouse down event (because an input hasn't stopped propagation of the mouse down
     * event).
     */
    onMouseDown = (event) => {
        if (event.handled) {
            // Reset the mouseDownFired state, so it doesn't stay as true on a future click
            this.mouseDownFired = false;
            return;
        }

        // This is added here due to this issue: https://github.com/Milanote/milanote/issues/3720
        // In Safari when text is selected and a drag start the browser seems to get confused and think text is being
        // dragged rather than the element div.
        // By clearing the selection on mouse-down the browser no longer gets confused.
        // NOTE: onDragStart is too late as the browser already thinks text is being dragged by then.
        // NOTE 2: This works because text input fields stop propagation onMouseDown so this handler doesn't get fired
        // when interacting with text fields.  If that proves to be incorrect we'll have to revisit this.
        clearSelection();

        this.mouseDownFired = true;

        // NOTE: event.stopPropagation() used to be called below here, however it interfered with the React DnD touch
        //  backend when trying to use mouse events to drag. This is useful on iPads to avoid long delays when using
        //  the native HTML5 DnD backend.
        // This used to stop parent ElementContainers from handling mouse events when a child has already handled
        //  the mouse event. Now, that responsibility is taken by the "markEventAsHandled" call and the
        //  "if (event.handled)" logic higher up in this function.
        markEventAsHandled(event);

        const {
            isRemotelySelected,
            isLocked,
            stopEditing,
            documentMode,
            isEditing,
            isEditingChild,
            isSelected,
            element,
        } = this.props;
        if (documentMode) return;

        // don't select the element directly when it is locked
        if (isLocked && !isSelected) return;

        if (isTaskList(element) && isEditingChild) stopEditing();

        // For elements with multiple editable parts (e.g. links with captions), to avoid
        // switching to a selected state just for a moment, don't stop editing on mouse down
        if (isEditing && isElementInEditor(event.target)) return;

        if (!isRemotelySelected && isEditing) stopEditing();
    };

    onClick = (event) => {
        // If the mousedown event didn't occur on this element then don't handle
        if (!this.mouseDownFired) return;

        this.mouseDownFired = false;

        const { dispatchGetCurrentlyEditingElement, dispatchDeselectAllElements, elementId } = this.props;

        const currentlyEditingElement = dispatchGetCurrentlyEditingElement();

        // On a small screen its more likely that the user is simply tapping to deselect
        // or finish editing the element rather than wanting to select the new element
        if (
            currentlyEditingElement &&
            currentlyEditingElement !== elementId &&
            window.visualViewport.height <= SMALL_VISUAL_VIEWPORT_HEIGHT
        ) {
            dispatchDeselectAllElements();
            return;
        }

        // Clicks will be prevented during a resize as we don't want to select the element after resizing
        if (this.preventClick) return;

        this.selectThisElement(event);
    };

    onContextMenu = (event) => {
        const { element, isLocked, isEditing, isSelected, isEditable, dispatchGetSelectedElementIds } = this.props;
        // If the mousedown event didn't occur on this element then don't handle
        if (!this.mouseDownFired) return;

        this.mouseDownFired = false;

        if (
            isEditing || // Don't select the element if user is editing
            !isEditable || // Don't preventDefault if not editable
            isSelected || // Don't select the element on right click if it's already selected;
            isLocked || // The locked selection is already handled by the onMouseDown
            this.preventClick // Clicks will be prevented during a resize as we don't want to select the element after resizing
        )
            return;

        event.preventDefault();

        const selectedElementIds = dispatchGetSelectedElementIds();
        if (selectedElementIds.includes(getLocationParentId(element))) return;

        this.selectThisElement();
    };

    startEditing = ({ editorId, editorKey, inputType, editorFocusRect }) => {
        const { element, isEditable, isRemotelyActive, dispatchStartEditingElement } = this.props;

        if (!isEditable || isRemotelyActive) return;

        dispatchStartEditingElement({
            id: getElementId(element),
            editorId,
            editorKey,
            inputType,
            editorFocusRect,
        });
    };

    selectThisElement = (event) => {
        const {
            isFocusedForegroundElement,
            isRemotelyActive,
            element,
            permissions,
            getContextZoomScale,
            dispatchHandleSelectionModeSelectElement,
            dispatchShowGuestAppRegistrationForm,
            dispatchFocusElementStart,
            isPresentationModeEnabled,
            dispatchGetPresentationModeIsFocusModeAllowed,
        } = this.props;

        const elementId = getElementId(element);

        const shouldEnterFocusMode =
            isPresentationModeEnabled &&
            dispatchGetPresentationModeIsFocusModeAllowed() &&
            canFocusElement(element) &&
            !isFocusedForegroundElement &&
            event &&
            isPrimaryButton(event) &&
            !hasCommandModifier(event);

        if (shouldEnterFocusMode) {
            event && event.stopPropagation();
            event && event.preventDefault();

            const zoomScale = getContextZoomScale();

            const { measureElementRef, ...focusElementProps } = this.props;

            // Not sure if we should be filtering these props? E.g. Removing functions?
            dispatchFocusElementStart(elementId, zoomScale, focusElementProps);
            return;
        }

        if (!isBoard(element) && isReadOnly(permissions)) return dispatchShowGuestAppRegistrationForm();

        event && event.stopPropagation();

        if (isRemotelyActive) return;

        const selectionMode = getEventSelectionMode(event);

        return dispatchHandleSelectionModeSelectElement(elementId, selectionMode);
    };

    setIsBatchedRenderInProgress = (isBatchedRenderInProgress) => {
        this.setState({ isBatchedRenderInProgress });
    };

    elementEvents = {
        onMouseDown: this.onMouseDown,
        onClick: this.onClick,
        onDoubleClick: stopPropagationOnly,
        onContextMenu: this.onContextMenu,
    };

    onElementVisibilityChange = (isVisible) => {
        const { elementId, onVisibilityChange, isSafariThrashingFixEnabled } = this.props;

        if (isSafariThrashingFixEnabled) {
            measurementsRegistry.setElementContentVisible(elementId, isVisible);
        }

        onVisibilityChange?.(isVisible);
    };

    render() {
        const {
            element,
            elementLocalData,
            elementId,
            isDragging,
            connectDragSource,
            dragModifierKeys,
            isResizing,
            animating,
            isClipboardCut,
            shouldRenderPlaceholder,
            placeholderElement,
            allowElementPlaceholder,
            inTrash,
            boardSection,
            isSafariThrashingFixEnabled,
        } = this.props;

        if (isClipboardCut) return null;

        const DisplayElement = elementRegistry.getElementContainerComponent(getElementType(element));
        if (!DisplayElement) {
            logger.warn('Unable to find a display element', { element });
            return <div />;
        }

        const copyDrag = dragModifierKeys.get(MODIFIER_KEYS.altKey);

        const elementClasses = classNames('Element', {
            dragging: isDragging && !copyDrag,
            copy: isDragging && copyDrag,
            resizing: isResizing,
            hidden: getLocalHidden(elementLocalData),
        });

        const observerRootOptions = getObserverRootOptions(boardSection, inTrash);

        // Columns use orchestrated list, we don't set the visibility until it finishes rendering otherwise it might not render into the visible area
        const setContentVisibility = !allowElementPlaceholder && !this.state.isBatchedRenderInProgress;

        const elementRender = (
            <div ref={this.props.measureElementRef} className={elementClasses} data-element-id={getElementId(element)}>
                <ElementContentVisibleObserver
                    domRef={this.props.measureElementRef}
                    onVisibilityChange={this.onElementVisibilityChange}
                    enable={!isLine(element)}
                    observerRootOptions={observerRootOptions}
                    setContentVisibility={setContentVisibility}
                    elementId={elementId}
                    isSafariThrashingFixEnabled={isSafariThrashingFixEnabled}
                />
                <AnimationLock animating={animating}>
                    {shouldRenderPlaceholder ? (
                        placeholderElement
                    ) : (
                        <DisplayElement
                            selectThisElement={this.selectThisElement}
                            startEditing={this.startEditing}
                            elementEvents={this.elementEvents}
                            setIsBatchedRenderInProgress={this.setIsBatchedRenderInProgress}
                            {...this.props}
                        />
                    )}
                </AnimationLock>
            </div>
        );

        const wrappingFn = shouldAttachDragSource(this.props) && !isTask(element) ? connectDragSource : identity;
        return wrappingFn(elementRender);
    }
}

ElementContainer.propTypes = {
    element: PropTypes.object.isRequired,
    elementLocalData: PropTypes.object,
    elementId: PropTypes.string.isRequired,
    currentBoardId: PropTypes.string.isRequired,
    isRemotelyActive: PropTypes.bool,
    isFocusedForegroundElement: PropTypes.bool,
    isEditing: PropTypes.bool,
    isEditingChild: PropTypes.bool,
    isEditable: PropTypes.bool,
    isDraggable: PropTypes.bool,
    isSelected: PropTypes.bool,
    isSingleSelected: PropTypes.bool,
    isLocked: PropTypes.bool,
    isClipboardCut: PropTypes.bool,
    isOver: PropTypes.bool,
    isDragging: PropTypes.bool,
    isResizing: PropTypes.bool,
    isHovered: PropTypes.bool,
    isRemotelySelected: PropTypes.bool,
    permissions: PropTypes.number,
    remoteSelectionData: PropTypes.object,
    connectDropTarget: PropTypes.func,
    connectDragSource: PropTypes.func,
    connectDragPreview: PropTypes.func,
    dispatchHandleSelectionModeSelectElement: PropTypes.func,
    dispatchStartEditingElement: PropTypes.func,
    dispatchUpdateElement: PropTypes.func,
    dispatchStopEditing: PropTypes.func,
    dispatchDeselectAllElements: PropTypes.func,
    stopEditing: PropTypes.func,
    documentMode: PropTypes.bool,
    dragModifierKeys: PropTypes.object,
    dispatchGetSelectedElementIds: PropTypes.func,
    dispatchGetCurrentlyEditingElement: PropTypes.func,

    animating: PropTypes.bool,
    dispatchShowGuestAppRegistrationForm: PropTypes.func,
    measureElementRef: PropTypes.object,

    isPresentationModeEnabled: PropTypes.bool,
    getContextZoomScale: PropTypes.func,
    dispatchFocusElementStart: PropTypes.func,
    dispatchGetPresentationModeIsFocusModeAllowed: PropTypes.func,

    onVisibilityChange: PropTypes.func,
    shouldRenderPlaceholder: PropTypes.bool,
    placeholderElement: PropTypes.element,
    allowElementPlaceholder: PropTypes.bool,
    inTrash: PropTypes.bool,
    boardSection: PropTypes.string,

    isSafariThrashingFixEnabled: PropTypes.bool,
};

export default ElementContainer;
